import { Button, Input } from "reactstrap";
import React, { useContext, useEffect, useState } from "react";
import {
  requestClientCreateSite,
  requestClientUpdateSite,
} from "../../services/backendRequests";
import { useDispatch, useSelector } from "react-redux";

import { ANALYTICS_EVENTS } from "../../constants";
import PropTypes from "prop-types";
import { CfSelect as Select } from "../../components/CfSelect";
import { TrackingContext } from "../../analytics";
import { addDangerAlert } from "../../actions/alerts";
import { fetchCrops } from "../../actions/crops";
import { iso31661 } from "iso-3166";

const countryOptions = iso31661.map((c) => ({
  label: c.name,
  value: c.alpha3,
}));

// Create / edit a site under a campaign. `site` null => creation.
export const SiteForm = ({ contract, site, user, onSaved, onCancel }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useContext(TrackingContext);
  const isEdit = site?.id != null;

  const [displayName, setDisplayName] = useState(site?.display_name ?? "");
  const [country, setCountry] = useState(site?.country ?? null);
  const [crop, setCrop] = useState(site?.crop ?? "");

  // Crops come from the store the campaign page already fills (fetchCrops).
  // "No crop" is a real choice, not an absent value: a site can legitimately
  // have none, and the tile says so explicitly.
  const crops = useSelector((store) => store.crops.all) ?? [];
  // The campaign page only fetches crops when it has no contracts yet, and
  // contracts come from the persisted user slice: arriving with a warm cache
  // left this list empty. The form asks for what it needs.
  useEffect(() => {
    if (crops.length === 0) dispatch(fetchCrops());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  const cropOptions = [
    { label: "No crop", value: "" },
    ...[...crops]
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      .map(({ name }) => ({ label: name, value: name })),
  ];
  const [alert, setAlert] = useState("");

  const submit = () => {
    const payload = {
      ...(site ?? {}),
      id: site?.id ?? null,
      display_name: displayName.trim(),
      country,
      crop,
      nb_microplots: site?.nb_microplots ?? 0,
    };

    const duplicate = contract.sites?.find(
      (existing) =>
        existing.display_name.toLowerCase() ===
          payload.display_name.toLowerCase() && existing.id !== payload.id
    );
    if (duplicate) {
      setAlert("Site name must be unique");
      return;
    }

    const request = isEdit
      ? requestClientUpdateSite(payload, user)
      : requestClientCreateSite(contract, payload, user);

    if (!isEdit) trackEvent(ANALYTICS_EVENTS.CREATE_SITE);

    request
      .then(() => onSaved())
      .catch((error) => dispatch(addDangerAlert(error)));
  };

  return (
    <div className="cf-site-form">
      {alert && <div className="cf-form-alert">{alert}</div>}

      <div className="mb-3">
        <label className="form-label">
          Site name* <i className="text-muted">(must be unique)</i>
        </label>
        <Input
          placeholder="Site name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Country*</label>
        <Select
          isSearchable
          value={countryOptions.find((c) => c.value === country) ?? null}
          onChange={(option) => setCountry(option?.value ?? null)}
          options={countryOptions}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Crop</label>
        <Select
          isSearchable
          value={cropOptions.find((option) => option.value === crop) ?? null}
          onChange={(option) => setCrop(option?.value ?? "")}
          options={cropOptions}
        />
      </div>

      <div className="d-flex justify-content-end gap-2">
        <Button color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={submit}
          disabled={!displayName.trim() || country == null}
        >
          {isEdit ? (
            <>
              <i className="fa-regular fa-floppy-disk me-1" /> Save changes
            </>
          ) : (
            <>
              <i className="fa-solid fa-plus me-1" /> Create site
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

SiteForm.propTypes = {
  contract: PropTypes.object.isRequired,
  site: PropTypes.object,
  user: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
